import axios from "axios";
import API_BASE_URL from "../config/apiConfig";
import api from "./api";

export const fetchTripPrices = async (tripId, activeOnly = true) => {
  const { data } = await axios.get(
    `${API_BASE_URL}/api/trips/${tripId}/prices`,
    { params: { activeOnly } }
  );
  return Array.isArray(data) ? data : [];
};

export const fetchTripQuoteOptions = async (tripId) => {
  const { data } = await axios.get(
    `${API_BASE_URL}/api/trips/${tripId}/quote/options`
  );
  return data;
};

export const calculateTripQuote = async (tripId, payload) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/trips/${tripId}/quote`,
    payload
  );
  return data;
};

/**
 * Prices every hotel offered for one night in a single request. Doing it option by option from the
 * browser would be one round trip per hotel per night, on the customer's page.
 */
export const fetchHotelOptionPrices = async (tripId, payload) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/trips/${tripId}/quote/hotel-option-prices`,
    payload
  );
  return Array.isArray(data) ? data : [];
};

export const fetchTripRoomSuggestions = async (tripId, payload) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/trips/${tripId}/quote/room-suggestions`,
    payload
  );
  return data;
};

export const fetchTripRoomAvailability = async (tripId, travelDate, bookingId = null) => {
  const endpoint = bookingId
    ? `/api/admin/trips/${tripId}/quote/room-availability`
    : `${API_BASE_URL}/api/trips/${tripId}/quote/room-availability`;
  const client = bookingId ? api : axios;
  const { data } = await client.get(endpoint, { params: { travelDate, ...(bookingId ? { bookingId } : {}) } });
  return Array.isArray(data) ? data : [];
};

// Extension nights fall outside the trip's own nights and are picked by hotel rather than by room
// rate, so they are looked up by room category over the extension's own dates.
export const fetchExtensionRoomAvailability = async (roomCategoryIds, startDate, endDate, bookingId = null) => {
  const ids = Array.from(new Set((roomCategoryIds || []).filter((id) => id != null).map((id) => String(id))));
  if (!ids.length || !startDate || !endDate) return [];
  const endpoint = bookingId
    ? `/api/admin/trips/quote/extension-room-availability`
    : `${API_BASE_URL}/api/trips/quote/extension-room-availability`;
  const client = bookingId ? api : axios;
  const { data } = await client.get(endpoint, {
    params: {
      roomCategoryIds: ids.join(","),
      startDate,
      endDate,
      ...(bookingId ? { bookingId } : {}),
    },
  });
  return Array.isArray(data) ? data : [];
};

export const calculateAdminTripQuote = async (tripId, payload) => {
  const { data } = await api.post(`/api/admin/trips/${tripId}/quote`, payload, { timeout: 20000 });
  return data;
};

/**
 * The lead portal's quote. Like the customer's, except rooms below a hotel's minimum guests come back
 * priced with a warning instead of refused - the team can agree an exception with the hotel. Admin,
 * using the same portal, gets its own quote.
 */
export const calculateTeamTripQuote = async (team, tripId, payload) => {
  if (team === "ADMIN") return calculateAdminTripQuote(tripId, payload);
  const tokenKey = team === "CALL" ? "callTeamToken" : "chatTeamToken";
  const teamPath = team === "CALL" ? "call-team" : "chat-team";
  const { data } = await axios.post(`${API_BASE_URL}/api/${teamPath}/trips/${tripId}/quote`, payload, {
    headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey) || ""}` },
  });
  return data;
};
